import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import ConfirmDialog from '../../components/ConfirmDialog';
import { MATERIAL_TABS, MATERIAL_FIELDS, isFieldRequired } from './materialFieldConfig';
import { VENDOR_TABS, VENDOR_FIELDS } from './vendorFieldConfig';
import useVendorOptions from '../../hooks/useVendorOptions';
import { SearchSelect, resolveVendorOptions } from '../../components/VendorFieldInput';
import {
  checkGstConsistency,
  gstinPan,
  isValidGstin,
  stateNameFor,
  gstinStateCode,
} from '../../lib/gst';

const CONFIGS = {
  material: { tabs: MATERIAL_TABS, fields: MATERIAL_FIELDS, endpoint: '/material-requests' },
  vendor:   { tabs: VENDOR_TABS,   fields: VENDOR_FIELDS,   endpoint: '/vendor-requests' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyApprover = () => ({ id: Date.now() + Math.random(), name: '', email: '' });

const isApproverComplete = a => a.name.trim() !== '' && EMAIL_RE.test(a.email.trim());
const isApproverEmpty = a => a.name.trim() === '' && a.email.trim() === '';

const META = {
  material: { title: 'Material Master Request', category: 'Material Master', gradient: 'linear-gradient(135deg,#D95A1A,#e87a3a)' },
  vendor:   { title: 'Vendor Master Request',   category: 'Vendor Master',   gradient: 'linear-gradient(135deg,#1A2C80,#2d3fa0)' },
};

// ── Searchable Material Type dropdown ────────────────────────────────────────
const MaterialTypeSelect = ({ value, options, onChange }) => {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const wrapRef             = useRef(null);

  // When a value is already set, show it in the input
  const selectedLabel = value
    ? (() => { const t = options.find(o => o.code === value); return t ? `${t.code}${t.description ? ` – ${t.description}` : ''}` : value; })()
    : '';

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return options;
    return options.filter(o => o.code.includes(q) || o.description.toUpperCase().includes(q));
  }, [query, options]);

  const select = useCallback(code => {
    onChange(code);
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const displayValue = open ? query : selectedLabel;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={SS.inputWrap}>
        <input
          style={SS.input}
          value={displayValue}
          placeholder="Search by code or name…"
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
        />
        {value && (
          <button type="button" style={SS.clear} onClick={() => { onChange(''); setQuery(''); setOpen(false); }} title="Clear">✕</button>
        )}
        <span style={SS.arrow} onClick={() => setOpen(o => !o)}>▾</span>
      </div>
      {open && (
        <div style={SS.dropdown}>
          {filtered.length === 0
            ? <div style={SS.noMatch}>No match</div>
            : filtered.map(o => (
                <div
                  key={o.code}
                  style={{ ...SS.option, ...(o.code === value ? SS.optionActive : {}) }}
                  onMouseDown={() => select(o.code)}
                >
                  <span style={SS.optCode}>{o.code}</span>
                  {o.description && <span style={SS.optDesc}>{o.description}</span>}
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
};

const SS = {
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: {
    width: '100%', padding: '10px 56px 10px 13px', border: '1.5px solid #e2e8f0',
    borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff',
    boxSizing: 'border-box', outline: 'none',
  },
  clear: {
    position: 'absolute', right: 28, background: 'none', border: 'none',
    cursor: 'pointer', color: '#8892b0', fontSize: 12, padding: '0 4px', lineHeight: 1,
  },
  arrow: {
    position: 'absolute', right: 10, cursor: 'pointer',
    color: '#8892b0', fontSize: 12, userSelect: 'none',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8,
    boxShadow: '0 8px 24px rgba(11,21,84,0.12)', maxHeight: 220,
    overflowY: 'auto', marginTop: 4,
  },
  option: {
    display: 'flex', alignItems: 'baseline', gap: 8,
    padding: '9px 13px', cursor: 'pointer', fontSize: 13,
    borderBottom: '1px solid #f0f2f8',
  },
  optionActive: { background: '#f0f4ff' },
  optCode: { fontWeight: 700, color: '#0B1554', minWidth: 48, flexShrink: 0 },
  optDesc: { color: '#4a5568', fontSize: 12.5 },
  noMatch: { padding: '10px 13px', fontSize: 13, color: '#8892b0' },
};

// ─────────────────────────────────────────────────────────────────────────────

const MasterRequestForm = ({ type }) => {
  const meta = META[type];
  const { tabs, fields: allFields, endpoint } = CONFIGS[type];
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [data, setData] = useState({});
  const [approvers, setApprovers] = useState([emptyApprover()]);
  const [loading, setLoading] = useState(false);
  const [gstConfirm, setGstConfirm] = useState(false);
  const [dupResult, setDupResult] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [plants, setPlants] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [materialGroups1, setMaterialGroups1] = useState([]);
  const [materialGroups2, setMaterialGroups2] = useState([]);
  const [materialGroups3, setMaterialGroups3] = useState([]);
  const [materialGroups4, setMaterialGroups4] = useState([]);
  const [materialGroups5, setMaterialGroups5] = useState([]);
  const [controlCodes, setControlCodes] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [mrpTypes, setMrpTypes] = useState([]);
  const [valuationTypes, setValuationTypes] = useState([]);
  const [lotSizes, setLotSizes] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [distributionChannels, setDistributionChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const { companyCodes, bpGroupings, paymentTerms, purchasingOrgs, incoterms, planningGroups, regions, bankKeys, withholdingTaxTypes, withholdingTaxCodes, refetchPlanningGroups } = useVendorOptions(type === 'vendor');
  const [ifscStatus, setIfscStatus] = useState(null);
  // Fields the user has typed into by hand. A GSTIN only pre-fills a field that
  // is not in here, so an override is never silently undone — but "Re-apply
  // from GSTIN" clears the set and forces the derivation again.
  const [pinned, setPinned] = useState(() => new Set());
  const pinRef = useRef(pinned);
  pinRef.current = pinned;

  // ── Draft save/load ─────────────────────────────────────────────────────────────────────────────
  const DRAFT_KEY = `mdm_draft_${type}`;

  // Restore draft on mount
  useEffect(() => {
    api.get(`/drafts/${type}`).then(r => {
      if (!r.data) return;
      const { title: t, description: d, data: fd, approvers: ap } = r.data;
      if (t)  setTitle(t);
      if (d)  setDescription(d);
      if (fd) setData(fd);
      if (ap && ap.length) setApprovers(ap.map(a => ({ ...a, id: Date.now() + Math.random() })));
      toast.info('Draft restored', { autoClose: 2000 });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = async () => {
    try {
      await api.put(`/drafts/${type}`, { title, description, data, approvers });
      toast.success('Draft saved!', { autoClose: 2000 });
    } catch { toast.error('Could not save draft'); }
  };

  const discardDraft = async () => {
    try { await api.delete(`/drafts/${type}`); } catch { /* ignore */ }
    setTitle(''); setDescription(''); setData({});
    setApprovers([emptyApprover()]); setActiveTab(tabs[0].id); setPinned(new Set());
    setDupResult(null);
    toast.info('Draft discarded', { autoClose: 2000 });
  };

  useEffect(() => {
    if (type === 'material') {
      api.get('/sap/options/material-types').then(r => setMaterialTypes(r.data)).catch(() => {});
      api.get('/sap/options/plants').then(r => setPlants(r.data)).catch(() => {});
      api.get('/sap/options/sales-organizations').then(r => setSalesOrgs(r.data)).catch(() => {});
      api.get('/sap/options/material-group').then(r => setMaterialGroups(r.data)).catch(() => {});
      api.get('/sap/options/material-groups-1').then(r => setMaterialGroups1(r.data)).catch(() => {});
      api.get('/sap/options/material-groups-2').then(r => setMaterialGroups2(r.data)).catch(() => {});
      api.get('/sap/options/material-groups-3').then(r => setMaterialGroups3(r.data)).catch(() => {});
      api.get('/sap/options/material-groups-4').then(r => setMaterialGroups4(r.data)).catch(() => {});
      api.get('/sap/options/material-groups-5').then(r => setMaterialGroups5(r.data)).catch(() => {});
      api.get('/sap/options/control-codes').then(r => setControlCodes(r.data)).catch(() => {});
      api.get('/sap/options/uom').then(r => setUoms(r.data)).catch(() => {});
      api.get('/sap/options/mrp-types').then(r => setMrpTypes(r.data)).catch(() => {});
      api.get('/sap/options/valuation-types').then(r => setValuationTypes(r.data)).catch(() => {});
      api.get('/sap/options/lot-sizes').then(r => setLotSizes(r.data)).catch(() => {});
      api.get('/sap/options/inspection-types').then(r => setInspectionTypes(r.data)).catch(() => {});
    }
    if (type === 'vendor') {
    }
  }, [type]);

  const isVendor = type === 'vendor';
  const gstin = isVendor ? data.gstn_number || '' : '';

  // Rule 1 (Region) and Rule 2 (PAN) are both derivable from the GSTIN, so
  // derive them — but only into fields the user has not pinned.
  useEffect(() => {
    if (!isVendor || !isValidGstin(gstin)) return;
    const derived = {};
    const pan = gstinPan(gstin);
    const region = stateNameFor(gstinStateCode(gstin));
    if (pan && !pinRef.current.has('pan_number')) derived.pan_number = pan;
    if (region && !pinRef.current.has('region')) derived.region = region;
    if (!Object.keys(derived).length) return;
    setData(prev => ({ ...prev, ...derived }));
    // Only the GSTIN should retrigger this; `data` is the thing being written.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstin, isVendor]);

  const setField = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
    if (isVendor && (key === 'region' || key === 'pan_number'))
      setPinned(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  // Fields auto-filled from SAP MaterialTypeConfig — read-only only while they hold a SAP value
  const SAP_AUTOFILL_KEYS = ['material_group', 'procurement_type', 'price_control_indicator', 'valuation_class', 'valuation_class_prj_stock'];
  const [sapFilledKeys, setSapFilledKeys] = useState(new Set());

  // Auto-fill from MaterialTypeConfigSet when both material_type + plant are set
  const materialType = type === 'material' ? (data.material_type || '') : '';
  const plant        = type === 'material' ? (data.plant || '') : '';
  const salesOrg     = type === 'material' ? (data.sales_organization || '') : '';

  // Fetch distribution channels + divisions when sales org changes
  useEffect(() => {
    if (!salesOrg) {
      setDistributionChannels([]); setDivisions([]);
      setData(prev => ({ ...prev, distribution_channel: '', division: '' }));
      return;
    }
    api.get('/sap/options/sales-area-config', { params: { salesOrg } })
      .then(r => { setDistributionChannels(r.data.distribution_channels); setDivisions(r.data.divisions); })
      .catch(() => { setDistributionChannels([]); setDivisions([]); });
    setData(prev => ({ ...prev, distribution_channel: '', division: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrg]);

  // Fetch storage locations + profit centers whenever plant changes
  useEffect(() => {
    if (!plant) {
      setStorageLocations([]);
      setProfitCenters([]);
      setData(prev => ({ ...prev, storage_location: '', profit_center: '' }));
      return;
    }
    api.get('/sap/options/storage-locations', { params: { plant } })
      .then(r => setStorageLocations(r.data)).catch(() => setStorageLocations([]));
    api.get('/sap/options/profit-centers', { params: { plant } })
      .then(r => setProfitCenters(r.data)).catch(() => setProfitCenters([]));
    setData(prev => ({ ...prev, storage_location: '', profit_center: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant]);
  useEffect(() => {
    if (!materialType || !plant) { setSapFilledKeys(new Set()); return; }
    api.get('/sap/options/material-type-config', { params: { materialType, plant } })
      .then(r => {
        if (!r.data) return;
        const filled = new Set();
        setData(prev => {
          const patch = {};
          for (const k of SAP_AUTOFILL_KEYS) {
            if (r.data[k]) { patch[k] = r.data[k]; filled.add(k); }
          }
          return { ...prev, ...patch };
        });
        setSapFilledKeys(filled);
        if (filled.size) toast.info('Fields auto-filled from Material Type + Plant', { autoClose: 2500 });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialType, plant]);

  // Re-fetch planning groups when company_code changes
  const vendorCompanyCode = type === 'vendor' ? (data.company_code || '') : '';
  useEffect(() => {
    if (!isVendor) return;
    refetchPlanningGroups(vendorCompanyCode);
    setData(prev => ({ ...prev, planning_group: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCompanyCode, isVendor]);

  // IFSC auto-fill from SAP BankKeySet
  const ifscValue = isVendor ? (data.bank_key_ifsc || '') : '';
  useEffect(() => {
    const code = ifscValue.trim().toUpperCase();
    if (!code) { setIfscStatus(null); return; }
    const found = bankKeys.find(b => b.code === code);
    if (found) {
      setData(prev => ({ ...prev, bank_id: found.description, bank_country_region_key: found.country || 'IN' }));
      setIfscStatus('ok');
      return;
    }
    setIfscStatus(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ifscValue, bankKeys, isVendor]);

  const reapplyFromGstin = () => {
    if (!isValidGstin(gstin)) {
      toast.error('Enter a valid 15-character GSTIN first');
      return;
    }
    setPinned(new Set());
    const pan = gstinPan(gstin);
    const region = stateNameFor(gstinStateCode(gstin));
    setData(prev => ({ ...prev, ...(pan ? { pan_number: pan } : {}), ...(region ? { region: region } : {}) }));
    toast.success('Region and PAN re-applied from the GSTIN');
  };

  const gstProblems = useMemo(
    () => (isVendor ? checkGstConsistency({ region: data.region, pan: data.pan_number, gstin }) : []),
    [isVendor, data.region, data.pan_number, gstin]
  );

  const visibleFields = tabs.find(t => t.id === activeTab).fields;

  const filledCount = useMemo(
    () => allFields.filter(f => String(data[f.key] ?? '').trim() !== '').length,
    [data, allFields]
  );

  const isZemp = isVendor && data.bp_grouping === 'ZEMP';

  const missingRequired = useMemo(
    () => allFields
      .filter(f => {
        if (type === 'vendor' && f.key === 'business_partner_number') {
          if (!isZemp) return false;
          return String(data[f.key] ?? '').trim() === '';
        }
        const req = type === 'material'
          ? isFieldRequired(f, data.material_type)
          : f.required;
        if (!req) return false;
        if (f.type === 'yesno') return data[f.key] !== 'X' && data[f.key] !== '';
        return String(data[f.key] ?? '').trim() === '';
      })
      .map(f => f.label),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, allFields, type, isZemp]
  );

  const setApprover = (id, key, value) =>
    setApprovers(prev => prev.map(a => (a.id === id ? { ...a, [key]: value } : a)));

  const addApprover = () => setApprovers(prev => [...prev, emptyApprover()]);

  const removeApprover = id =>
    setApprovers(prev => (prev.length === 1 ? prev : prev.filter(a => a.id !== id)));

  const clearAll = () => {
    setTitle(''); setDescription(''); setData({});
    setApprovers([emptyApprover()]); setActiveTab(tabs[0].id); setPinned(new Set());
    setDupResult(null);
  };

  // Auto duplicate check — fires 800ms after the user stops typing the description
  const isMaterial = type === 'material';
  const materialDesc = isMaterial ? (data.material_description || '') : '';
  const materialUom  = isMaterial ? (data.base_uom || '') : '';

  useEffect(() => {
    if (!isMaterial || !materialDesc.trim()) { setDupResult(null); return; }
    setDupResult(null);
    setDupChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data: result } = await api.post('/sap/check-duplicate', { description: materialDesc.trim(), uom: materialUom || '' });
        setDupResult(result);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Duplicate check failed');
      } finally { setDupChecking(false); }
    }, 800);
    return () => { clearTimeout(timer); setDupChecking(false); };
  }, [materialDesc, materialUom, isMaterial]);

  const canSubmit = !isMaterial || (!dupChecking && dupResult?.can_create !== false);

  const handleSubmit = async e => {
    e.preventDefault();
    const used = approvers.filter(a => !isApproverEmpty(a));
    if (!used.some(isApproverComplete)) {
      toast.error('Please fill Approver Name and a valid Approver Email');
      return;
    }
    if (used.some(a => !isApproverComplete(a))) {
      toast.error('Please complete or remove the incomplete approver row(s)');
      return;
    }
    if (missingRequired.length) {
      toast.error(`Please fill: ${missingRequired.join(', ')}`);
      const firstMissing = allFields.find(f => f.label === missingRequired[0]);
      const owner = firstMissing && tabs.find(t => t.fields.some(f => f.key === firstMissing.key));
      if (owner) setActiveTab(owner.id);
      return;
    }
    if (gstProblems.length && !gstConfirm) {
      if (data.gstn_number) setActiveTab('tax');
      else setActiveTab('address');
      setGstConfirm(true);
      return;
    }
    setGstConfirm(false);
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(data).filter(([, v]) => String(v ?? '').trim() !== '')
      );
      await api.post(endpoint, {
        title,
        description,
        approvers: used.map(a => ({ name: a.name.trim(), email: a.email.trim() })),
        data: payload,
      });
      toast.success('Request submitted successfully!');
      try { await api.delete(`/drafts/${type}`); } catch { /* ignore */ }
      setTitle(''); setDescription(''); setData({}); setApprovers([emptyApprover()]); setPinned(new Set());
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally { setLoading(false); }
  };

  return (
    <div>
      {gstConfirm && (
        <ConfirmDialog
          message={`GST checks flagged:\n• ${gstProblems.join('\n• ')}\n\nSubmit anyway?`}
          onConfirm={() => { setGstConfirm(false); handleSubmit({ preventDefault: () => {} }); }}
          onCancel={() => setGstConfirm(false)}
        />
      )}
      {/* Header banner */}
      <div style={{ ...S.banner, background: meta.gradient }}>
        <div style={S.bannerCircle} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>{meta.title}</div>
          <div style={S.bannerSub}>Fill in the details below to submit a new {meta.category} request</div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Request info card */}
        <div style={S.card}>
          <div style={S.cardHeader}>Request Information</div>
          <div style={S.field}>
            <label style={S.label}>Request Title <span style={S.req}>*</span></label>
            <input style={S.input} value={title} onChange={e => setTitle(e.target.value)}
              required placeholder={`e.g. New ${meta.category} — XYZ Corp`} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Description / Justification</label>
            <textarea style={{ ...S.input, height: 90, resize: 'vertical' }} value={description}
              onChange={e => setDescription(e.target.value)} placeholder="Provide additional context or business justification..." />
          </div>
        </div>

        {/* Approver card */}
        <div style={S.card}>
          <div style={S.cardHeaderRow}>
            <span style={S.cardHeaderInline}>Approver Details</span>
            <span style={S.counter}>{approvers.filter(isApproverComplete).length} added</span>
          </div>

          {approvers.map((a, i) => (
            <div key={a.id} style={S.approverRow}>
              <span style={S.approverIndex}>{i + 1}</span>
              <div style={S.approverGrid}>
                <div style={S.field}>
                  <label style={S.label}>Approver Name <span style={S.req}>*</span></label>
                  <input style={S.input} value={a.name}
                    onChange={e => setApprover(a.id, 'name', e.target.value)}
                    placeholder="Enter Approver Name" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Approver Email <span style={S.req}>*</span></label>
                  <input style={S.input} type="email" value={a.email}
                    onChange={e => setApprover(a.id, 'email', e.target.value)}
                    placeholder="e.g. approver@company.com" />
                </div>
              </div>
              <button type="button" onClick={() => removeApprover(a.id)}
                disabled={approvers.length === 1}
                style={{ ...S.removeApprover, opacity: approvers.length === 1 ? 0.3 : 1 }}
                title={approvers.length === 1 ? 'At least one approver is required' : 'Remove approver'}>
                ✕
              </button>
            </div>
          ))}

          <button type="button" style={S.addApprover} onClick={addApprover}>
            <span style={S.addApproverIcon}>+</span> Add Approver
          </button>
        </div>

        {/* Details card */}
        <div style={S.card}>
          <div style={S.cardHeaderRow}>
            <span style={S.cardHeaderInline}>{meta.category} Details</span>
            <span style={S.counter}>
              {filledCount} / {allFields.length} fields filled
            </span>
          </div>

          <div style={S.tabBar}>
            {tabs.map(t => {
              const active = t.id === activeTab;
              const filled = t.fields.filter(f => String(data[f.key] ?? '').trim() !== '').length;
              const pending = t.fields.filter(f => {
                if (type === 'vendor' && f.key === 'business_partner_number')
                  return isZemp && String(data[f.key] ?? '').trim() === '';
                const req = type === 'material'
                  ? isFieldRequired(f, data.material_type)
                  : f.required;
                return req && String(data[f.key] ?? '').trim() === '';
              }).length;
              return (
                <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                  style={{
                    ...S.tab,
                    ...(active ? { ...S.tabActive, boxShadow: `inset 0 -3px 0 ${meta.gradient}` } : null),
                  }}>
                  {t.label}
                  {filled > 0 && <span style={S.tabCount}>{filled}</span>}
                  {pending > 0 && <span style={S.tabDot} title={`${pending} required field(s) pending`} />}
                </button>
              );
            })}
          </div>

          <div style={S.grid}>
            {visibleFields.map(f => (
              <div key={f.key} style={S.field}>
                <label style={S.label}>
                  {f.label}
                  {type === 'vendor' && f.key === 'business_partner_number'
                    ? isZemp && <span style={S.req}>*</span>
                    : (type === 'material' ? isFieldRequired(f, data.material_type) : f.required) && <span style={S.req}>*</span>
                  }
                </label>
                {type === 'material' && f.key === 'material_type' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={materialTypes}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'plant' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={plants}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'storage_location' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={storageLocations}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'profit_center' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={profitCenters}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'sales_organization' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={salesOrgs}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'material_group' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={materialGroups}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'material_group_1' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={materialGroups1} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'material_group_2' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={materialGroups2} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'material_group_3' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={materialGroups3} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'material_group_4' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={materialGroups4} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'material_group_5' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={materialGroups5} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'lot_size' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={lotSizes} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'inspection_type' ? (
                  <MaterialTypeSelect value={data[f.key] || ''} options={inspectionTypes} onChange={v => setField(f.key, v)} />
                ) : type === 'material' && f.key === 'control_code' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={controlCodes}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && (f.key === 'base_uom' || f.key === 'weight_unit') ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={uoms}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'mrp_type' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={mrpTypes}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'valuation_type' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={valuationTypes}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'distribution_channel' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={distributionChannels}
                    onChange={v => setField(f.key, v)}
                  />
                ) : type === 'material' && f.key === 'division' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={divisions}
                    onChange={v => setField(f.key, v)}
                  />
                ) : f.type === 'yesno' ? (
                  <div style={S.radioGroup}>
                    {['Yes', 'No'].map(opt => (
                      <label key={opt} style={S.radioLabel}>
                        <input
                          type="radio"
                          name={f.key}
                          value={opt}
                          checked={opt === 'Yes' ? data[f.key] === 'X' : data[f.key] === ''}
                          onChange={() => setField(f.key, opt === 'Yes' ? 'X' : '')}
                          style={S.radioInput}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                ) : type === 'vendor' && f.key === 'bank_key_ifsc' ? (
                  <div>
                    <SearchSelect
                      value={data[f.key] || ''}
                      options={bankKeys}
                      onChange={v => setField(f.key, v)}
                    />
                     {ifscStatus === 'ok' && <div style={{ fontSize: 11.5, color: '#1A2C80', marginTop: 4, fontWeight: 600 }}>✓ Bank Name auto-filled</div>}

                  </div>
                ) : type === 'vendor' && f.key === 'region' ? (
                  <SearchSelect value={data[f.key] || ''} options={regions} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'planning_group' ? (
                  <SearchSelect value={data[f.key] || ''} options={planningGroups} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'incoterms' ? (
                  <SearchSelect value={data[f.key] || ''} options={incoterms} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'purchase_organisation' ? (
                  <SearchSelect value={data[f.key] || ''} options={purchasingOrgs} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && (f.key === 'terms_of_payment_key' || f.key === 'payment_terms') ? (
                  <SearchSelect value={data[f.key] || ''} options={paymentTerms} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'bp_grouping' ? (
                  <SearchSelect value={data[f.key] || ''} options={bpGroupings} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'company_code' ? (
                  <SearchSelect value={data[f.key] || ''} options={companyCodes} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'business_partner_number' ? (
                  isZemp
                    ? <input
                        style={S.input}
                        value={data[f.key] || ''}
                        onChange={e => setField(f.key, e.target.value)}
                        placeholder="Enter Business Partner Number"
                      />
                    : <input
                        style={{ ...S.input, ...S.inputReadOnly }}
                        value="System Generated"
                        readOnly
                      />
                ) : type === 'vendor' && f.key === 'withholding_tax_type' ? (
                  <SearchSelect value={data[f.key] || ''} options={withholdingTaxTypes} onChange={v => setField(f.key, v)} />
                ) : type === 'vendor' && f.key === 'withholding_tax_code_type' ? (
                  <SearchSelect value={data[f.key] || ''} options={withholdingTaxCodes} onChange={v => setField(f.key, v)} />
                ) : f.type === 'searchselect' ? (
                  <MaterialTypeSelect
                    value={data[f.key] || ''}
                    options={f.options}
                    onChange={v => setField(f.key, v)}
                  />
                ) : (
                  <input
                    style={{ ...S.input, ...(f.readOnly || sapFilledKeys.has(f.key) ? S.inputReadOnly : null) }}
                    type={f.type || 'text'}
                    value={data[f.key] || ''}
                    readOnly={f.readOnly || sapFilledKeys.has(f.key)}
                    onChange={e => setField(f.key, e.target.value)}
                    placeholder={f.readOnly || sapFilledKeys.has(f.key) ? 'Auto-filled from SAP' : `Enter ${f.label}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* GST consistency panel — vendor only */}
        {isVendor && (isValidGstin(gstin) || String(data.gstn_number || '').trim()) && (
          <div style={S.card}>
            <div style={S.cardHeaderRow}>
              <span style={S.cardHeaderInline}>GST Consistency Check</span>
              {isValidGstin(gstin) && (
                <span style={S.counter}>
                  State code {gstinStateCode(gstin)} · PAN {gstinPan(gstin)}
                </span>
              )}
            </div>

            {isValidGstin(gstin) && (
              <div style={S.gstOk}>
                GSTIN reads as {stateNameFor(gstinStateCode(gstin))} · PAN {gstinPan(gstin)}.
                {' '}Region and PAN were filled in from it — edit them if the vendor differs.
              </div>
            )}
            {!isValidGstin(gstin) && (
              <div style={S.gstWarn}>
                GST Tax Type and GSTN Number must be a 15-character GSTIN
                (for example 29ABCDE1234F1Z5) for Region and PAN to be checked.
              </div>
            )}
            {gstProblems.map(p => (
              <div key={p} style={S.gstProblem}>⚠ {p}</div>
            ))}

            <div style={S.gstRow}>
              <button type="button" onClick={reapplyFromGstin} style={S.gstBtn}>
                Re-apply Region &amp; PAN from GSTIN
              </button>
              {pinned.size > 0 && (
                <span style={S.gstPinned}>
                  Edited by hand: {[...pinned].map(k => (k === 'region' ? 'Region' : 'PAN')).join(', ')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Duplicate check — material only, auto-triggered */}
        {isMaterial && materialDesc.trim() && (
          <div style={S.card}>
            <div style={S.cardHeaderRow}>
              <span style={S.cardHeaderInline}>Duplicate Check</span>
              {dupChecking && <span style={S.dupChecking}>Checking…</span>}
            </div>

            {!dupChecking && dupResult?.status === 'EXACT_DUPLICATE' && (
              <div style={S.dupExact}>
                <div style={S.dupExactTitle}>❌ Material already exists</div>
                {dupResult.matches.map(m => (
                  <div key={m.material} style={S.dupRow}>
                    <span style={S.dupMat}>Material: <strong>{m.material}</strong></span>
                    <span style={S.dupDesc}>Description: {m.description}</span>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: '#9a3412', marginTop: 8, fontWeight: 600 }}>Creation is blocked.</div>
              </div>
            )}

            {!dupChecking && dupResult?.status === 'SIMILAR_FOUND' && (
              <div style={S.dupSimilar}>
                <div style={S.dupSimilarTitle}>⚠ Similar materials found</div>
                <table style={S.dupTable}>
                  <thead>
                    <tr>
                      <th style={S.dupTh}>Material</th>
                      <th style={S.dupTh}>Description</th>
                      <th style={S.dupTh}>Similarity</th>
                      <th style={S.dupTh}>SAP UOM</th>
                      <th style={S.dupTh}>Your UOM</th>
                      <th style={S.dupTh}>UOM Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dupResult.matches.map(m => (
                      <tr key={m.material}>
                        <td style={S.dupTd}>{m.material}</td>
                        <td style={S.dupTd}>{m.description}</td>
                        <td style={{ ...S.dupTd, fontWeight: 700, color: m.similarity >= 95 ? '#dc2626' : '#d97706' }}>{m.similarity}%</td>
                        <td style={S.dupTd}>{m.sap_uom || '—'}</td>
                        <td style={S.dupTd}>{m.user_uom || '—'}</td>
                         <td style={{ ...S.dupTd, fontWeight: 700, color: m.uom_match ? '#4f6ef7' : '#dc2626' }}>{m.uom_match ? '✓' : '✗'}</td>

                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 10 }}>
                  Review the above before continuing. You may still submit if none of these match your request.
                </div>
              </div>
            )}

            {!dupChecking && dupResult?.status === 'NO_MATCH' && (
              <div style={S.dupOk}>✓ No duplicates found — you may proceed to submit.</div>
            )}
          </div>
        )}

        <div style={S.actions}>
          <button style={{ ...S.btn, background: meta.gradient, opacity: (loading || !canSubmit) ? 0.5 : 1 }}
            type="submit" disabled={loading || !canSubmit}>
            {loading ? 'Submitting...' : `Submit ${meta.category} Request`}
          </button>
          <button type="button" style={S.btnDraft} onClick={saveDraft}>
            💾 Save Draft
          </button>
          <button type="button" style={S.btnSecondary} onClick={discardDraft}>
            Discard Draft
          </button>
        </div>
      </form>
    </div>
  );
};

const S = {
  banner: {
    borderRadius: 14, padding: '24px 28px', marginBottom: 20,
    position: 'relative', overflow: 'hidden',
  },
  bannerCircle: {
    position: 'absolute', width: 200, height: 200, borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)', top: -60, right: -40,
  },
  bannerTitle: { color: '#fff', fontSize: 20, fontWeight: 800, marginBottom: 4 },
  bannerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  card: {
    background: '#fff', borderRadius: 14, padding: '20px 24px',
    marginBottom: 16, boxShadow: '0 2px 12px rgba(11,21,84,0.06)',
  },
  cardHeader: {
    fontSize: 12, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8,
    textTransform: 'uppercase', marginBottom: 18, paddingBottom: 12,
    borderBottom: '1px solid #f0f2f8',
  },
  cardHeaderRow: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 8, marginBottom: 14, paddingBottom: 12,
    borderBottom: '1px solid #f0f2f8',
  },
  cardHeaderInline: {
    fontSize: 12, fontWeight: 800, color: '#0B1554', letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  counter: { fontSize: 12, fontWeight: 600, color: '#8892b0' },
  tabBar: {
    display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 18,
    borderBottom: '1px solid #eef1f7',
  },
  tab: {
    position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
    padding: '9px 14px', fontSize: 12.5, fontWeight: 700, color: '#8892b0',
    borderRadius: '8px 8px 0 0', transition: 'color 0.2s', whiteSpace: 'nowrap',
  },
  tabActive: { color: '#0B1554', background: '#f8faff' },
  tabCount: {
    display: 'inline-block', minWidth: 16, padding: '0 5px', marginLeft: 6,
    borderRadius: 8, background: '#eef1f7', color: '#8892b0',
    fontSize: 10.5, fontWeight: 700, textAlign: 'center', verticalAlign: 'middle',
  },
  tabDot: {
    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
    background: '#DD3222', marginLeft: 6, verticalAlign: 'middle',
  },
  inputReadOnly: { background: '#eef1f7', color: '#8892b0', cursor: 'not-allowed' },
  select: { width: '100%', padding: '10px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box', cursor: 'pointer' },
  gstOk: { fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', marginBottom: 8, lineHeight: 1.5 },
  gstWarn: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px', marginBottom: 8, lineHeight: 1.5 },
  gstProblem: { fontSize: 12.5, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '9px 12px', marginBottom: 6, lineHeight: 1.5 },
  gstRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4 },
  gstBtn: {
    padding: '9px 16px', color: '#1A2C80', background: '#fff', cursor: 'pointer',
    border: '1.5px solid #c3cbe0', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
  },
  gstPinned: { fontSize: 11.5, color: '#8892b0', fontWeight: 600 },
  approverRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 14px', marginBottom: 10, borderRadius: 12,
    background: '#f8faff', border: '1px solid #eef1f7',
  },
  approverIndex: {
    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
    background: '#0B1554', color: '#fff', fontSize: 11, fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 22,
  },
  approverGrid: { flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', minWidth: 0 },
  removeApprover: {
    flexShrink: 0, width: 28, height: 28, marginTop: 22, borderRadius: 8,
    border: '1.5px solid #e2e8f0', background: '#fff', color: '#DD3222',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  addApprover: {
    display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4,
    padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
    color: '#0B1554', background: '#fff', cursor: 'pointer',
    border: '1.5px dashed #c3cbe0', transition: 'all 0.2s',
  },
  addApproverIcon: { fontSize: 16, fontWeight: 800, lineHeight: 1 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 5 },
  req: { color: '#DD3222' },
  input: {
    width: '100%', padding: '10px 13px', border: '1.5px solid #e2e8f0',
    borderRadius: 8, fontSize: 13.5, color: '#0B1554', background: '#f8faff',
    boxSizing: 'border-box', transition: 'all 0.2s',
  },
  actions: { display: 'flex', gap: 12, alignItems: 'center' },
  btn: {
    padding: '12px 32px', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
  },
  btnSecondary: {
    padding: '12px 24px', color: '#DD3222', border: '1.5px solid #fecaca',
    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#fff5f5',
  },
  btnDraft: {
    padding: '12px 24px', color: '#1A2C80', border: '1.5px solid #c3cbe0',
    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#f0f4ff',
  },
  dupChecking: { fontSize: 12, fontWeight: 700, color: '#4f6ef7', background: '#f0f4ff', padding: '3px 10px', borderRadius: 20 },
  dupExact: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px' },
  dupExactTitle: { fontSize: 13, fontWeight: 800, color: '#dc2626', marginBottom: 8 },
  dupRow: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 },
  dupMat: { fontSize: 12.5, color: '#374151' },
  dupDesc: { fontSize: 12.5, color: '#374151' },
  dupSimilar: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px' },
  dupSimilarTitle: { fontSize: 13, fontWeight: 800, color: '#b45309', marginBottom: 10 },
  dupTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  dupTh: { textAlign: 'left', padding: '6px 10px', background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 11, borderBottom: '1px solid #fde68a' },
  dupTd: { padding: '7px 10px', borderBottom: '1px solid #fef3c7', color: '#374151' },
  ifscBadge: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 700, color: '#4f6ef7', whiteSpace: 'nowrap' },
  radioGroup: { display: 'flex', gap: 24, alignItems: 'center', padding: '10px 0' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#0B1554', cursor: 'pointer', fontWeight: 600 },
  radioInput: { width: 16, height: 16, accentColor: '#1A2C80', cursor: 'pointer' },
  dupOk: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#15803d' },
};

export default MasterRequestForm;
